import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Switch,
    Tab,
    Tabs,
    TextField,
    Typography,
    FormControlLabel
} from "@material-ui/core";
import { useHistory, useParams } from "react-router-dom";
import { PageTitle } from "../../../components/PageTitle";
import { BackButton } from "../../../components/BackButton";
import { useAlert } from "../../../hooks/useAlert";
import { routes } from "../../../routes";
import { LeakCheckForm } from "../../../entities/business/LeakCheckFormTemplate";
import { LeakCheckService } from "../../../services/business/LeakCheckService";
import OverlayLoadingScreen from "../../../components/OverlayLoadingScreen";
import { FetchOptionalResult } from "../../../services/server/WebClient";

// Import our new components
import { LeakCheckFormProvider, useLeakCheckForm } from "./LeakCheckFormContext";
import { CustomerInfoSection } from "./CustomerInfoSection";
import { TankCheckSection } from "./TankCheckSection";
import { PressureTestsSection } from "./PressureTestsSection";
import { RegulatorCheckSection } from "./RegulatorCheckSection";
import { CustomSectionsPanel } from "./CustomSectionsPanel";
import { Prompt } from "react-router-dom";

// URL parameter type
interface LeakCheckFormDetailParams {
    formId: string;
}

// Tab panel for organizing sections
interface TabPanelProps {
    children?: React.ReactNode;
    index: any;
    value: any;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
            style={{ paddingTop: 20 }}
        >
            {value === index && <>{children}</>}
        </div>
    );
}

function a11yProps(index: any) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

export function LeakCheckFormDetailPage() {
    const { formId } = useParams<LeakCheckFormDetailParams>();
    const alert = useAlert();
    const history = useHistory();
    const [result, setResult] = useState<FetchOptionalResult<LeakCheckForm>>();
    const [form, setForm] = useState<LeakCheckForm | null>(null);
    const [tabValue, setTabValue] = useState(0);

    // Load form on component mount
    useEffect(() => {
        async function loadForm() {
            const formResult = await LeakCheckService.getForm(formId);
            setResult(formResult);
            
            if (formResult.success) {
                setForm(formResult.data);
            } else if (formResult.type === "missing") {
                alert.error("Form not found. It may have been deleted.");
            } else {
                alert.serverError(formResult);
            }
        }
        
        loadForm();
    }, [formId, alert]);

    const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setTabValue(newValue);
    };

    // Return to list page
    const handleBack = () => {
        history.push(routes.business.resolve.leakCheckFormsListPage());
    };

    // Show loading while data is being fetched
    if (!result) return <OverlayLoadingScreen />;

    // Form not found
    if (!result.success || !form) {
        return (
            <Grid container spacing={3}>
                <PageTitle title="Form Not Found" />
                <Grid item xs={12}>
                    <Grid container alignItems="center">
                        <Grid item>
                            <BackButton onClick={handleBack} />
                        </Grid>
                        <Grid item>
                            <Typography variant="h2">Form Not Found</Typography>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography>
                                The leak check form you are looking for could not be found. It may have been deleted.
                            </Typography>
                            <Box mt={2}>
                                <Button variant="contained" color="primary" onClick={handleBack}>
                                    Back to forms
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        );
    }

    return (
        <LeakCheckFormProvider initialForm={form}>
            <FormDetailContent 
                tabValue={tabValue} 
                handleTabChange={handleTabChange} 
                handleBack={handleBack}
            />
        </LeakCheckFormProvider>
    );
}

// Main content with access to the context
const FormDetailContent: React.FC<{
    tabValue: number;
    handleTabChange: (event: React.ChangeEvent<{}>, newValue: number) => void;
    handleBack: () => void;
}> = ({ tabValue, handleTabChange, handleBack }) => {
    const { form, isDirty } = useLeakCheckForm();
    
    if (!form) return null;
    
    return (
        <Grid container spacing={3}>
            {/* React Router's Prompt component to show browser confirmation */}
            <Prompt
                when={isDirty}
                message="You have unsaved changes. Are you sure you want to leave this page?"
            />
            
            <PageTitle title={`Edit Leak Check Form - ${form.name}`} />
            <Grid item xs={12}>
                <Box mb={3}>
                    <Grid container alignItems="center" spacing={2}>
                        <Grid item>
                            <BackButton onClick={handleBack} />
                        </Grid>
                        <Grid item>
                            <Typography variant="h2">{form.name}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <FormHeader />
                        </Grid>
                    </Grid>
                </Box>
            </Grid>

            <Grid item xs={12}>
                <Box mb={3}>
                    <FormDetailsCard />
                </Box>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <Box>
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            indicatorColor="primary"
                            textColor="primary"
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            <Tab label="Customer Info" {...a11yProps(0)} />
                            <Tab label="Tank Check" {...a11yProps(1)} />
                            <Tab label="Pressure Tests" {...a11yProps(2)} />
                            <Tab label="Regulator Check" {...a11yProps(3)} />
                            <Tab label="Custom Sections" {...a11yProps(4)} />
                        </Tabs>
                    </Box>

                    <CardContent>
                        {/* Customer Info Section */}
                        <TabPanel value={tabValue} index={0}>
                            <CustomerInfoSection />
                        </TabPanel>

                        {/* Tank Check Section */}
                        <TabPanel value={tabValue} index={1}>
                            <TankCheckSection />
                        </TabPanel>

                        {/* Pressure Tests Section */}
                        <TabPanel value={tabValue} index={2}>
                            <PressureTestsSection />
                        </TabPanel>

                        {/* Regulator Check Section */}
                        <TabPanel value={tabValue} index={3}>
                            <RegulatorCheckSection />
                        </TabPanel>

                        {/* Custom Sections */}
                        <TabPanel value={tabValue} index={4}>
                            <CustomSectionsPanel />
                        </TabPanel>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};

// Extract these small components for better organization
const FormHeader: React.FC = () => {
    const { form, toggleActive, saveForm, saving } = useLeakCheckForm();
    
    return (
        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
            <FormControlLabel
                control={
                    <Switch
                        checked={form?.isActive}
                        onChange={toggleActive}
                        color="primary"
                    />
                }
                label={form?.isActive ? "Active" : "Inactive"}
            />
            <Button 
                variant="contained" 
                color="primary" 
                onClick={saveForm}
                disabled={saving}
            >
                Save Form
            </Button>
        </Box>
    );
};

const FormDetailsCard: React.FC = () => {
    const { form, updateName } = useLeakCheckForm();
    
    if (!form) return null;
    
    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Form Details
                </Typography>
                <TextField
                    label="Form Name"
                    value={form.name}
                    onChange={(e) => updateName(e.target.value)}
                    fullWidth
                    required
                    variant="outlined"
                    margin="normal"
                />
                <Box mt={2}>
                    <Typography variant="body2" color="textSecondary">
                        Created: {form.createdDate.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Last Modified: {form.lastModifiedDate.toLocaleString()}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};